import React, { useState, useEffect } from 'react';
import { Star, MessageSquare, Send, Settings, ThumbsUp, ExternalLink, Zap } from 'lucide-react';
import { safeAsync, Badge, KPI, neuShadow, neuShadowSm, neuShadowInset, inp, btnPrimary, btnSm, btnDanger, cardWrap, emptyMsg, overlayStyle, modalStyle, modalHeader, modalTitle, closeBtn } from './crmShared';

export default function ReputationTab({ contacts, canCreate, canUpdate, onContactClick }) {
  const [tab, setTab] = useState('reviews');
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({});
  const [requests, setRequests] = useState([]);
  const [settings, setSettings] = useState({});
  const [respondingId, setRespondingId] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterRating, setFilterRating] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    if (!window.zignagenixAPI) return;
    (async () => {
      try {
        const [reviewData, statsData, requestData, settingsData] = await Promise.all([
          window.zignagenixAPI.getCRMReviews(),
          window.zignagenixAPI.getCRMReviewStats(),
          window.zignagenixAPI.getCRMReviewRequests(),
          window.zignagenixAPI.getCRMReviewSettings(),
        ]);
        // Coerce array-typed payloads — a truthy non-array (error envelope,
        // stub response shape, etc.) would otherwise pass `|| []` and crash
        // the subsequent .map() / .filter() calls with "x.map is not a
        // function". Same defensive pattern as MembershipsTab.
        setReviews(Array.isArray(reviewData) ? reviewData : []);
        setStats(statsData && typeof statsData === 'object' && !Array.isArray(statsData) ? statsData : {});
        setRequests(Array.isArray(requestData) ? requestData : []);
        setSettings(settingsData && typeof settingsData === 'object' && !Array.isArray(settingsData) ? settingsData : {});
      } catch (err) {
        console.error('[ReputationTab] Load error:', err);
      }
    })();
    /*
     * ⚠ Empty deps: the fetch takes no filters any more, so re-running it on
     * every dropdown change would be a pointless round trip. The filtering is
     * derived state (visibleReviews), recomputed on render.
     *
     * ⛔ NO `eslint-disable` HERE. `react-hooks/exhaustive-deps` is NOT a
     * configured rule in this project — only `rules-of-hooks` is — and
     * disabling an UNDEFINED rule is itself a build ERROR
     * ("Definition for rule ... was not found"). The same note is at
     * CRMPage.js:1292, and I reintroduced the mistake here before the
     * production build caught it. Unit and react tests pass either way;
     * `npm run build` is the only gate that sees it.
     */
  }, []);

  const handleRespond = async () => {
    if (!responseText.trim()) return;
    /*
     * ⚠⚠ THE SECOND ARGUMENT IS A STRING, NOT AN OBJECT. crm:reviews:respond
     * runs `UPDATE crm_reviews SET business_response = ?` with whatever it is
     * handed. The old call passed `{ responseText }`, so once this wrapper
     * reached a real handler the column would have been filled with
     * "[object Object]" — a silent button replaced by a corrupted record,
     * which is the worse trade. Found by reading the handler's signature
     * rather than by running it.
     *
     * ⛔ And this writes a column. It does NOT post the reply to Google or
     * Facebook — nothing in the handler reaches a review site.
     */
    const result = await safeAsync(
      () => window.zignagenixAPI.respondToCRMReview(respondingId, responseText),
    )();
    if (!result) return;
    setRespondingId(null);
    setResponseText('');
    const updated = await safeAsync(() => window.zignagenixAPI.getCRMReviews())();
    setReviews(Array.isArray(updated) ? updated : []);
  };

  // ⛔ UNREACHABLE BY DESIGN — the control that called this is disabled in the
  // render below. sendCRMAIChat points at the unregistered crm:entity:action,
  // and the only real AI path (routes/crm_ai.js → _ai_shared → Bedrock) is
  // OUTWARD, which the ruling's boundary forbids wiring from this tab.
  // eslint-disable-next-line no-unused-vars
  const handleAISuggest = async () => {
    const review = reviews.find(r => r.id === respondingId);
    if (!review) return;
    setAiLoading(true);
    await safeAsync(async () => {
      const resp = await window.zignagenixAPI.sendCRMAIChat({
        context: `You are a professional review response specialist. Generate a concise, warm, and professional response to this review.`,
        messages: [{ role: 'user', content: `Review: "${review.text}" (${review.rating} stars)` }],
      });
      setResponseText(resp?.text || '');
    })();
    setAiLoading(false);
  };

  /*
   * ⛔ THIS LOGS. IT DOES NOT SEND. crm:review-requests:send
   * (routes/crm.js:12044) runs one INSERT into crm_review_requests with
   * status='sent' and writes an audit row. No SMS and no email go out — the
   * handler touches neither _dispatch_sms nor _dispatch_email.
   * ⚠ And crm_review_settings.auto_request, the toggle that looks like it
   * would automate this, is written by the settings form and READ BY NOTHING
   * (card P71 Step 0). Turning it on changes no behaviour.
   */
  /*
   * ⛔⛔ THE RESULT IS ACTED ON, NOT DISCARDED — review item 2.
   *
   * This used to await the call inside a safeAsync CALLBACK and throw the
   * value away. safeAsync inspects what its callback RETURNS, so an
   * { error: true } body was swallowed: the modal closed and the list
   * refreshed exactly as if the request had been logged. The most convincing
   * possible failure — the screen performing success.
   *
   * ⇒ The API call IS the safeAsync body, and the modal only closes when the
   * result is truthy (safeAsync returns undefined for a throw AND for an
   * error body, having already shown it).
   */
  const handleLogRequest = async (data) => {
    const result = await safeAsync(() => window.zignagenixAPI.sendCRMReviewRequest(data))();
    if (!result) return;
    setShowNewRequest(false);
    const updated = await safeAsync(() => window.zignagenixAPI.getCRMReviewRequests())();
    setRequests(Array.isArray(updated) ? updated : []);
  };

  const handleUpdateSettings = async (settingKey, value) => {
    const updatedSettings = { ...settings, [settingKey]: value };
    const result = await safeAsync(
      () => window.zignagenixAPI.updateCRMReviewSettings(updatedSettings),
    )();
    // ⚠ Only reflect the toggle once the server accepted it. Setting state
    // first made a refused write look applied until the next page load.
    if (result) setSettings(updatedSettings);
  };

  /*
   * ⛔⛔ THE DROPDOWNS FILTER HERE, BECAUSE THE SERVER CANNOT — review round 2.
   *
   * crm:reviews:list takes NO arguments: `SELECT * FROM crm_reviews ORDER BY
   * created_at DESC`. The old call passed { platform, rating, status } to the
   * phantom channel, so the question of whether anything read them never came
   * up. Re-pointing it in P71a made the dropdowns LOOK functional and do
   * nothing — a filter that silently returns the unfiltered set is worse than
   * one that is greyed out, because the staff member believes the list in
   * front of them is the answer to the question they asked.
   *
   * ⇒ Filtered on the client, over the same three fields the dropdowns offer.
   * The set is one org's reviews and is small; this is not a pagination
   * problem. Pushing the predicate into the handler would be the better fix
   * and is a server change this card's ruling does not cover.
   *
   * ⚠ rating is compared as a NUMBER — the <option value> is a string.
   */
  const visibleReviews = reviews.filter((r) => {
    if (filterPlatform && String(r.platform || '') !== filterPlatform) return false;
    if (filterRating && Number(r.rating) !== Number(filterRating)) return false;
    if (filterStatus && String(r.status || '') !== filterStatus) return false;
    return true;
  });

  const avgRating = stats.avg_rating || 0;
  const totalReviews = stats.total_reviews || 0;
  const responseRate = stats.response_rate || 0;
  const pendingRequests = stats.pending_requests || 0;

  const starDisplay = Array.from({ length: 5 }, (_, i) => (
    <span key={i} style={{ color: i < Math.round(avgRating) ? '#F26522' : '#D0D5DD', fontSize: 16 }}>★</span>
  )).reduce((a, b) => [a, b], []);

  return (
    <div style={{ padding: '20px', background: '#FFFFFF' }}>
      {/* Card P71 — the boundary, stated once at the top of the tab. */}
      <div
        data-testid="reputation-not-sent-banner"
        style={{
          background: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E',
          borderRadius: 6, padding: '8px 10px', fontSize: 12, marginBottom: 16,
        }}
      >
        This records the request. It does not send a message. <strong>Log review request</strong>{' '}
        writes a row in the CRM only — no SMS and no email go out.
      </div>
      {/* KPIs */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <KPI icon={Star} label="Avg Rating" value={<div style={{display:'flex',alignItems:'center',gap:4}}>{avgRating.toFixed(1)} {starDisplay}</div>} color="#F26522" />
        <KPI icon={MessageSquare} label="Total Reviews" value={totalReviews} color="#2E8B98" />
        <KPI icon={ThumbsUp} label="Response Rate" value={`${responseRate}%`} color="#62BB46" />
        <KPI icon={Send} label="Pending Requests" value={pendingRequests} color="#F26522" />
      </div>

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, borderBottom: '2px solid #E5E7EB' }}>
        {['reviews', 'requests', 'settings'].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '10px 16px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              color: tab === t ? '#3CA0AE' : '#6C757D',
              borderBottom: tab === t ? '3px solid #3CA0AE' : 'none',
              transition: 'all 0.2s',
            }}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* Reviews Tab */}
      {tab === 'reviews' && (
        <div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <select value={filterPlatform} onChange={e => setFilterPlatform(e.target.value)} style={{ ...inp, flex: 1, minWidth: 120 }}>
              <option value="">All Platforms</option>
              <option value="google">Google</option>
              <option value="facebook">Facebook</option>
              <option value="yelp">Yelp</option>
              <option value="healthgrades">Healthgrades</option>
            </select>
            <select value={filterRating} onChange={e => setFilterRating(e.target.value)} style={{ ...inp, flex: 1, minWidth: 100 }}>
              <option value="">All Ratings</option>
              {[5, 4, 3, 2, 1].map(r => <option key={r} value={r}>{r} Stars</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inp, flex: 1, minWidth: 120 }}>
              <option value="">All Status</option>
              <option value="new">New</option>
              <option value="read">Read</option>
              <option value="responded">Responded</option>
            </select>
          </div>

          {visibleReviews.length === 0 ? (
            <div style={emptyMsg}>No reviews found</div>
          ) : (
            visibleReviews.map(review => (
              <div key={review.id} style={{ ...cardWrap, padding: 16, marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#212529', marginBottom: 4 }}>{review.reviewer_name}</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ color: '#F26522', fontSize: 14 }}>
                        {Array.from({ length: 5 }, (_, i) => i < review.rating ? '★' : '☆').join('')}
                      </span>
                      <Badge text={review.platform} color="#256F7A" />
                      {review.status === 'responded' && <Badge text="Responded" color="#62BB46" />}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: '#ADB5BD' }}>{new Date(review.date).toLocaleDateString()}</div>
                </div>
                <p style={{ margin: '8px 0 12px', fontSize: 13, color: '#4B5563', lineHeight: 1.5 }}>{review.text}</p>
                {review.response_text && (
                  <div style={{ background: '#E8F4F8', padding: 10, borderRadius: 8, marginBottom: 12, borderLeft: '3px solid #3CA0AE' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#2E8B98', marginBottom: 4 }}>Your Response</div>
                    <div style={{ fontSize: 12, color: '#4B5563' }}>{review.response_text}</div>
                  </div>
                )}
                {!review.response_text && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => setRespondingId(review.id)} style={btnPrimary}>
                      <MessageSquare size={12} /> Respond
                    </button>
                    {/*
                      ⛔ DISABLED, TWO INDEPENDENT REASONS (card P71a).
                      1. sendCRMAIChat points at crm:entity:action, which is
                         registered nowhere — it has never drafted anything.
                      2. The only real AI path (routes/crm_ai.js → _ai_shared
                         → Bedrock) is OUTWARD, and the ruling's boundary
                         forbids wiring an outward sender from this tab.
                    */}
                    <button
                      onClick={undefined}
                      disabled
                      title="Not available yet — AI drafting is not connected from this tab."
                      style={{ ...btnSm, opacity: 0.5, cursor: 'not-allowed' }}
                    >
                      <Zap size={12} /> AI Suggest — Not available yet
                    </button>
                  </div>
                )}
              </div>
            ))
          )}

          {/* Response Panel */}
          {respondingId && (
            <div style={overlayStyle} onClick={() => { setRespondingId(null); setResponseText(''); }}>
              <div style={modalStyle} onClick={e => e.stopPropagation()}>
                <div style={modalHeader}>
                  <h3 style={modalTitle}>Respond to Review</h3>
                  <button style={closeBtn} onClick={() => { setRespondingId(null); setResponseText(''); }}>✕</button>
                </div>
                <textarea
                  value={responseText}
                  onChange={e => setResponseText(e.target.value)}
                  placeholder="Write your professional response..."
                  style={{ ...inp, minHeight: 120, resize: 'vertical', marginBottom: 16, fontFamily: 'inherit' }}
                />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={() => { setRespondingId(null); setResponseText(''); }} style={btnSm}>Cancel</button>
                  <button onClick={handleRespond} style={btnPrimary}>Submit Response</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Review Requests Tab */}
      {tab === 'requests' && (
        <div>
          {/*
            ⚠ RELABELLED TOO. This opener said "Send Review Request" and sits
            one sub-tab away from the submit control P71 already relabelled —
            so the flow read "Send… → Log…", which is worse than either wording
            alone. It was missed in P71 because the enumeration test only
            rendered the DEFAULT sub-tab; that test now walks all three.
          */}
          {canCreate && (
            <button
              onClick={() => setShowNewRequest(true)}
              title="Log review request — records it in the CRM. No message is sent."
              style={{ ...btnPrimary, marginBottom: 16 }}
            >
              <Send size={14} /> Log review request
            </button>
          )}

          {requests.length === 0 ? (
            <div style={emptyMsg}>No review requests sent</div>
          ) : (
            requests.map(req => (
              <div key={req.id} style={{ ...cardWrap, padding: 14, marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#212529', marginBottom: 4 }}>
                    {req.contact_id ? (
                      <span onClick={() => onContactClick && onContactClick(req.contact_id)} style={{ cursor: 'pointer', color: '#2E8B98', fontWeight: 600 }}>
                        {req.contact_name}
                      </span>
                    ) : (
                      req.contact_name
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: '#6C757D' }}>
                    <Badge text={req.channel} color="#2E8B98" />
                    <span>{req.platform}</span>
                    <span>{new Date(req.date).toLocaleDateString()}</span>
                  </div>
                </div>
                <Badge text={req.status} color={req.status === 'pending' ? '#F26522' : '#62BB46'} />
              </div>
            ))
          )}

          {/* New Request Modal */}
          {showNewRequest && (
            <NewRequestModal
              contacts={contacts}
              onClose={() => setShowNewRequest(false)}
              onSubmit={handleLogRequest}
            />
          )}
        </div>
      )}

      {/* Settings Tab */}
      {tab === 'settings' && (
        <div>
          {['google', 'facebook', 'healthgrades'].map(platform => (
            <div key={platform} style={{ ...cardWrap, padding: 16, marginBottom: 16 }}>
              <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#212529', textTransform: 'capitalize' }}>
                {platform} Configuration
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 4 }}>Review URL</label>
                  <input
                    type="text"
                    defaultValue={settings[`${platform}_url`] || ''}
                    onChange={e => handleUpdateSettings(`${platform}_url`, e.target.value)}
                    placeholder="https://..."
                    style={inp}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 4 }}>Min Rating Threshold</label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    defaultValue={settings[`${platform}_min_rating`] || 3}
                    onChange={e => handleUpdateSettings(`${platform}_min_rating`, parseInt(e.target.value))}
                    style={inp}
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#4B5563', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    defaultChecked={settings[`${platform}_auto_send`] || false}
                    onChange={e => handleUpdateSettings(`${platform}_auto_send`, e.target.checked)}
                  />
                  Auto-send requests
                </label>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 4 }}>Send Delay (hours)</label>
                  <input
                    type="number"
                    min="0"
                    defaultValue={settings[`${platform}_delay_hours`] || 24}
                    onChange={e => handleUpdateSettings(`${platform}_delay_hours`, parseInt(e.target.value))}
                    style={inp}
                  />
                </div>
              </div>
            </div>
          ))}

          <div style={{ ...cardWrap, padding: 16, marginBottom: 16 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#212529' }}>Email Template</h4>
            <textarea
              defaultValue={settings.email_template || 'Hi {first_name}, please leave a review: {review_url}'}
              onChange={e => handleUpdateSettings('email_template', e.target.value)}
              placeholder="Use {first_name} and {review_url} as merge fields"
              style={{ ...inp, minHeight: 100, resize: 'vertical', marginBottom: 8, fontFamily: 'monospace', fontSize: 12 }}
            />
            <div style={{ fontSize: 11, color: '#6C757D' }}>Available merge fields: {'{first_name}'}, {'{review_url}'}</div>
          </div>

          <div style={{ ...cardWrap, padding: 16 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#212529' }}>SMS Template</h4>
            <textarea
              defaultValue={settings.sms_template || 'Hi {first_name}, review us here: {review_url}'}
              onChange={e => handleUpdateSettings('sms_template', e.target.value)}
              placeholder="Use {first_name} and {review_url} as merge fields"
              style={{ ...inp, minHeight: 80, resize: 'vertical', marginBottom: 8, fontFamily: 'monospace', fontSize: 12 }}
            />
            <div style={{ fontSize: 11, color: '#6C757D' }}>Available merge fields: {'{first_name}'}, {'{review_url}'}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function NewRequestModal({ contacts, onClose, onSubmit }) {
  const [selectedContact, setSelectedContact] = useState('');
  const [channel, setChannel] = useState('email');
  const [platform, setPlatform] = useState('google');
  const [customMsg, setCustomMsg] = useState('');

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <h3 style={modalTitle}>Send Review Request</h3>
          <button style={closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 6 }}>Contact</label>
            <select value={selectedContact} onChange={e => setSelectedContact(e.target.value)} style={inp}>
              <option value="">Select a contact...</option>
              {contacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 6 }}>Channel</label>
            <select value={channel} onChange={e => setChannel(e.target.value)} style={inp}>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 6 }}>Platform</label>
            <select value={platform} onChange={e => setPlatform(e.target.value)} style={inp}>
              <option value="google">Google</option>
              <option value="facebook">Facebook</option>
              <option value="yelp">Yelp</option>
              <option value="healthgrades">Healthgrades</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 6 }}>Custom Message (optional)</label>
            <textarea value={customMsg} onChange={e => setCustomMsg(e.target.value)} placeholder="Add a personal message..." style={{ ...inp, minHeight: 80, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btnSm}>Cancel</button>
          <button
            onClick={() => {
              onSubmit({ contact_id: selectedContact, channel, platform, custom_message: customMsg });
            }}
            style={btnPrimary}
            disabled={!selectedContact}
            title="Log review request — records it in the CRM. No message is sent."
          >
            Log review request
          </button>
        </div>
      </div>
    </div>
  );
}
